import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useAuth, type SubscriptionSummary } from '../lib/auth';
import { money0 } from '../lib/format';

/** The same address the footer and the registration page point at. */
const SUPPORT_URL = 'https://www.europiumsolutions.com/contact-us/';

/**
 * The prompt a hotel sees on its first visit, before it has paid for anything.
 *
 * Registration takes no money, so a hotel arrives either inside a trial or
 * waiting to pay. Either way the product is open in front of them with no
 * indication that a step remains, and the only place that says so is a page
 * they have no reason to visit. This says it once, on the screen they land on,
 * with the plan they actually chose.
 *
 * Shown while nothing has been paid for and dismissed for the rest of the
 * browsing session. Not remembered longer than that on purpose: an unpaid
 * account is worth mentioning again tomorrow, and a nag that can be silenced
 * for good is a nag nobody acts on.
 */

/** Which tier this is, for the badge. Falls back for a plan added later. */
const PLAN_GLYPHS: Record<string, string> = {
  FREE: '✦',
  STARTER: '⌂',
  GROWTH: '◱',
  PROFESSIONAL: '♛',
  ENTERPRISE: '▦',
};

const TONES: Record<string, string> = {
  FREE: 'tone-brand',
  STARTER: 'tone-blue',
  GROWTH: 'tone-green',
  PROFESSIONAL: 'tone-violet',
  ENTERPRISE: 'tone-amber',
};

/**
 * Nothing has been paid for yet.
 *
 * `currentPeriodEnd` is set only by a settled payment, so its absence is what
 * separates a brand-new hotel from one that has been paying for a year and
 * happens to be overdue this month.
 */
export const awaitingFirstPayment = (subscription: SubscriptionSummary | null): boolean =>
  Boolean(
    subscription &&
      subscription.currentPeriodEnd === null &&
      /*
       * A hotel that has already authorised a standing instruction has done
       * this step. Nothing has been charged yet - that waits for the trial to
       * end - but telling them to "complete payment" when they have set it up
       * and are waiting is nagging them for somebody else's homework.
       */
      !subscription.autoRenew &&
      (subscription.status === 'TRIALING' || subscription.status === 'PENDING_PAYMENT'),
  );

/** "Up to 3 properties", "1 property", "Up to 10 users" - a limit, said again. */
const LIMIT_LINE = /\d+\s+(propert(y|ies)|users?)\b/i;

/**
 * Per account as well as per plan. Keyed on the plan alone, closing it for one
 * hotel silenced it for the next one registered in the same tab - which is
 * exactly what happens when somebody signs up, signs out and signs up again.
 */
const dismissKey = (subscription: SubscriptionSummary, userId: string | undefined) =>
  `sarthi.activate.${userId ?? 'anon'}.${subscription.planCode}`;

export function ActivatePlanPrompt() {
  const { subscription, session } = useAuth();
  const [dismissed, setDismissed] = useState(true);

  /*
   * Only where the product is actually open behind it.
   *
   * A hotel on a plan with no trial cannot get in at all, and already gets a
   * full screen explaining exactly this with the same link - putting a modal
   * over it would say everything twice. This is for someone inside a trial:
   * using SARTHI, with a step still outstanding that nothing else mentions.
   */
  const shouldShow = awaitingFirstPayment(subscription) && Boolean(subscription?.isEntitled);

  useEffect(() => {
    if (!shouldShow || !subscription) return;
    try {
      setDismissed(sessionStorage.getItem(dismissKey(subscription, session?.user.id)) === '1');
    } catch {
      // Private browsing, or storage blocked. Showing it is the safe side.
      setDismissed(false);
    }
  }, [shouldShow, subscription, session?.user.id]);

  if (!shouldShow || !subscription || dismissed) return null;

  const close = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem(dismissKey(subscription, session?.user.id), '1');
    } catch {
      /* not remembering it is survivable; it reappears on the next screen */
    }
  };

  const onTrial = subscription.status === 'TRIALING';
  const isOwner = session?.role === 'OWNER';
  const glyph = PLAN_GLYPHS[subscription.planCode] ?? '◆';
  const tone = TONES[subscription.planCode] ?? 'tone-brand';

  /*
   * Only what the plan lists. The two limits already have their own place
   * beside the price, and repeating them under "What's included" made the
   * whole block a restatement for any plan whose features are not filled in.
   *
   * A trial also drops any line that restates a property or user count. Those
   * lines are free text a superadmin wrote, and they drift from the real
   * limits shown beside the price (the free plan's said 3 properties while it
   * allowed 2) - two different numbers in one card is worse than one.
   */
  const included = onTrial
    ? subscription.features.filter((line) => !LIMIT_LINE.test(line))
    : subscription.features;

  return createPortal(
    <div className="scrim activate-scrim" onMouseDown={(e) => e.target === e.currentTarget && close()}>
      <div className="activate-modal" role="dialog" aria-modal="true" aria-label={onTrial ? 'Welcome to SARTHI' : 'Activate your plan'}>
        <button className="btn ghost icon sm activate-close" onClick={close} aria-label="Close">
          ✕
        </button>

        <div className="activate-body">
          <div className="activate-pitch">
            <div className="brand-row">
              <span className="brand-mark" aria-hidden="true">
                S
              </span>
              <b>SARTHI</b>
            </div>

            {/*
             * A trial is the whole product, open, with nothing owed. Calling it
             * "almost there" with a payment step still to do told a hotel it
             * was missing something it was not.
             */}
            {onTrial ? (
              <h2>
                Welcome to SARTHI!
                <br />
                <span className="accent">Your free trial</span> is ready
              </h2>
            ) : (
              <h2>
                You&rsquo;re almost there!
                <br />
                <span className="accent">Activate your plan</span> to get started
              </h2>
            )}
            <p className="small muted">
              Your account is created and verified.{' '}
              {onTrial
                ? `Every feature is open for your ${subscription.trialDays}-day trial — explore the platform at your own pace.`
                : `To start using SARTHI, please complete the payment for your selected plan.`}
            </p>

            <ol className="activate-steps">
              <li className="done">
                <span className="activate-tick" aria-hidden="true">
                  ✓
                </span>
                <span>
                  <b>Account created</b>
                  <span className="xs muted">Your email has been verified.</span>
                </span>
              </li>
              <li className="done">
                <span className="activate-tick" aria-hidden="true">
                  ✓
                </span>
                <span>
                  <b>Plan selected</b>
                  <span className="xs muted">
                    You&rsquo;ve chosen the {subscription.planName} plan
                    {onTrial && subscription.trialDays > 0
                      ? ` (${subscription.trialDays}-day free trial)`
                      : ''}
                    .
                  </span>
                </span>
              </li>
              {onTrial ? null : (
                <li>
                  <span className="activate-tick pending" aria-hidden="true">
                    3
                  </span>
                  <span>
                    <b>Complete payment</b>
                    <span className="xs muted">Activate your plan to unlock all features.</span>
                  </span>
                </li>
              )}
            </ol>
          </div>

          <aside className="activate-plan">
            <div className="row gap-6 wrap" style={{ justifyContent: 'space-between' }}>
              <span className="chip ok">Your selected plan</span>
              {subscription.trialDays > 0 ? (
                <span className="chip info">{subscription.trialDays}-day free trial</span>
              ) : null}
            </div>

            <div className="activate-plan-head">
              <span className={`plan-icon ${tone}`} aria-hidden="true">
                {glyph}
              </span>
              <span className="grow">
                <b className="activate-plan-name">{subscription.planName}</b>
                {subscription.planDescription ? (
                  <span className="xs muted plan-row-sub">{subscription.planDescription}</span>
                ) : null}
              </span>
            </div>

            <div className="activate-price">
              <span>
                <b>{subscription.priceMonthly > 0 ? money0(subscription.priceMonthly) : '₹0'}</b>
                <span className="xs muted plan-row-sub">per month</span>
              </span>
              <span className="activate-limits">
                <span>
                  Up to {subscription.limits.maxProperties}{' '}
                  {subscription.limits.maxProperties === 1 ? 'property' : 'properties'}
                </span>
                <span>Up to {subscription.limits.maxUsers} users</span>
              </span>
            </div>

            {included.length > 0 ? (
              <>
                <div className="b">What&rsquo;s included</div>
                <ul className="plan-features activate-included">
                  {included.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
              </>
            ) : null}

            <div className="form-note">
              {onTrial
                ? `You can explore all features during your ${subscription.trialDays}-day free trial. No payment is charged today.`
                : 'Your plan starts the moment the payment goes through. Nothing has been charged yet.'}
            </div>

            {onTrial ? (
              <button type="button" className="btn primary lg activate-cta" onClick={close}>
                Explore the platform →
              </button>
            ) : isOwner ? (
              <Link className="btn primary lg activate-cta" to="/billing" onClick={close}>
                Proceed to Payment →
              </Link>
            ) : (
              <div className="form-note warn">
                Only an owner can complete this payment. Ask them to open Billing.
              </div>
            )}

            <div className="activate-assurances">
              <span>
                <b>Secure payment</b>
                <span className="xs muted">via Cashfree</span>
              </span>
              <span>
                <b>Instant invoice</b>
                <span className="xs muted">&amp; activation</span>
              </span>
              <span>
                <b>Change or upgrade</b>
                <span className="xs muted">anytime</span>
              </span>
            </div>
          </aside>
        </div>

        <div className="activate-foot">
          <span className="grow">
            <b>Need help choosing the right plan?</b>
            <span className="xs muted plan-row-sub">
              Our team is here to help you get started.
            </span>
          </span>
          <a className="btn" href={SUPPORT_URL} target="_blank" rel="noopener noreferrer">
            Contact Sales
          </a>
        </div>
      </div>
    </div>,
    document.body,
  );
}
